import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import BrandLogo from "../components/BrandLogo";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPassword() {
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("La password deve avere almeno 8 caratteri.");
    if (password !== confirm) return setError("Le password non coincidono.");
    setLoading(true);
    const res = await resetPassword(token, password);
    setLoading(false);
    if (res.ok) {
      setDone(true);
      toast.success("Password aggiornata");
      setTimeout(() => navigate("/login"), 2000);
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-sm" data-testid="reset-password-page">
        <div className="flex items-center gap-2 mb-8">
          <BrandLogo size="lg" />
        </div>

        {!token ? (
          <div className="flex items-start gap-2.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-3">
            <AlertCircle style={{ width: 18, height: 18 }} className="shrink-0 mt-0.5" />
            <span>Link non valido. Richiedi un nuovo link dalla pagina di accesso.</span>
          </div>
        ) : done ? (
          <div data-testid="reset-password-success" className="flex items-start gap-2.5 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-3">
            <CheckCircle2 style={{ width: 18, height: 18 }} className="shrink-0 mt-0.5" />
            <span>Password aggiornata con successo. Ti stiamo reindirizzando all'accesso…</span>
          </div>
        ) : (
          <>
            <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Reimposta la password</h1>
            <p className="text-sm text-slate-500 mt-1 mb-8">Scegli una nuova password per il tuo account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="new-password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Nuova password
                </Label>
                <Input
                  id="new-password"
                  type="password"
                  data-testid="reset-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm-password" className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Conferma password
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  data-testid="reset-password-confirm-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="mt-1.5"
                  required
                />
              </div>
              {error && (
                <p data-testid="reset-password-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <Button type="submit" data-testid="reset-password-submit" disabled={loading} className="w-full h-11 text-sm font-semibold">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Aggiorna password"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
